import { useCallback, useMemo, useSyncExternalStore } from "react";
import * as imageCacheStorage from "src/utils/imageCacheStorage.js";
import { resolvePictureUrl, withParam } from "src/utils/imageUrl.js";
import { forgetProcessedImage } from "src/utils/useProcessedImage.js";
import { forgetCachedImage } from "src/utils/Utils.js";

const profileImageVersions = new Map();
const profileImageSubscribers = new Set();

const subscribeToProfileImageChanges = (listener) => {
  profileImageSubscribers.add(listener);
  return () => profileImageSubscribers.delete(listener);
};

/**
 * Invalidates every cached URL variant for a profile picture and updates mounted
 * consumers even when the uploaded file keeps the same storage filename.
 * @param {string} picture - The creator.picture value used by useProfileImage.
 */
export const invalidateProfileImage = (picture) => {
  if (!picture) return;

  // Keyed by the resolved URL so a bare filename and its absolute form share a
  // version: callers pass whichever they hold.
  const imageUrl = resolvePictureUrl(picture);
  const previousVersion = profileImageVersions.get(imageUrl) ?? 0;

  // Drop the persisted and in-memory bytes for both the outgoing version and the
  // bare URL, or the replacement upload keeps resolving to the image it replaced.
  const staleUrls = previousVersion
    ? [imageUrl, withParam(imageUrl, "version", previousVersion)]
    : [imageUrl];
  staleUrls.forEach((url) => {
    imageCacheStorage.evict(url);
    forgetProcessedImage(url);
    forgetCachedImage(url);
  });

  profileImageVersions.set(imageUrl, previousVersion + 1);
  profileImageSubscribers.forEach((listener) => listener());
};

/**
 * Builds a creator's profile-image URL.
 * @param {object} creator - Object carrying a `picture` filename or absolute URL.
 * @param {{ perMountTimeStamp?: boolean }} [options] - Opt into a per-mount timestamp.
 * @returns {string|null} The image URL, or null when the creator has no picture.
 */
export const useProfileImage = (
  creator,
  { perMountTimeStamp } = { perMountTimeStamp: false }
) => {
  const picture = creator?.picture;
  const getSnapshot = useCallback(
    () => profileImageVersions.get(resolvePictureUrl(picture)) ?? 0,
    [picture]
  );
  const profileImageVersion = useSyncExternalStore(
    subscribeToProfileImageChanges,
    getSnapshot,
    getSnapshot
  );

  return useMemo(() => {
    const imageUrl = resolvePictureUrl(picture);
    if (!imageUrl) return null;

    // The URL stays stable so every cache layer can hold it. An explicit version
    // changes it immediately after an upload, even when the filename does not;
    // a per-mount timestamp additionally defeats the browser's own cache.
    const versioned = profileImageVersion
      ? withParam(imageUrl, "version", profileImageVersion)
      : imageUrl;

    return perMountTimeStamp
      ? withParam(versioned, "timestamp", Date.now())
      : versioned;
  }, [picture, perMountTimeStamp, profileImageVersion]);
};
